import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Button,
    Group,
    SimpleGrid,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useTopLevelDomains} from '../hooks/useTopLevelDomains';
import {useTopLevelDomainsSelection} from '../hooks/useTopLevelDomainsSelection';
import {useTopLevelDomainsTableColumns} from '../hooks/useTopLevelDomainsTableColumns';
import {TopLevelDomain} from '../types';
import {useVerifiedPermissions} from '@flexinet/auth-components';

// FNP-1292 — the top-level-domains table, on the SHARED component.
//
// ⚠️ WHAT THE HAND-ROLLED `<Table>` MADE IMPOSSIBLE, not merely absent: sorting
// (no `sortStatus` contract to wire), token pagination (`SimpleTablePagination`
// is offset-shaped and the service discarded `nextToken`), and the distinct
// empty and error states (`TableBody` renders one blank body for all three).
// Those boxes close as a consequence of this one change; they were never
// independent defects. Same migration registrars had.
export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
}) => {

    const {allowedActions} = useVerifiedPermissions();

    const {columns} = useTopLevelDomainsTableColumns();

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        isReloading,
        nextToken,
        fetchNextPage,
        fetchRecords,
        handleFilterByKeyword,
    } = useTopLevelDomains();

    const {
        selectedRecords,
        setSelectedRecords,
    } = useTopLevelDomainsSelection();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const searching = keyword.trim() !== '';

    const canDelete = allowedActions.includes('DeleteTopLevelDomain');

    // ⚠️ THE TWO EMPTY STATES MEAN OPPOSITE THINGS and `noRecordsText` is a
    // single string, so the distinction is branched here. A filtered empty
    // result is not "you have no top level domains" — telling an operator that
    // sends them hunting for a bug that is not there.
    const noRecordsText = searching
        ? I18n.get('No loaded top level domain matches this search')
        : I18n.get('No top level domains yet');

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>

            <Group justify={'space-between'} align={'center'}>
                <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>
                {extraTools}
            </Group>

            {/* ⚠️ DISTINCT FROM AN EMPTY TABLE: this is the QUERY failing, so
                nothing at all is known — not "you have no top level domains".
                It is reachable only because TopLevelDomainsService re-throws
                now; while it swallowed the error and returned undefined, a
                failed fetch rendered as an empty list. */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load top level domains')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your top level domains. The request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={fetchRecords}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<TopLevelDomain>
                columns={columns}
                records={visibleItems}
                idAccessor={'id'}
                selectedRecords={canDelete ? selectedRecords : undefined}
                onSelectedRecordsChange={canDelete ? setSelectedRecords : undefined}
                // Client-side sorting over the pages loaded so far — the same
                // scope the keyword works on. Token pagination cannot reorder
                // rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                // ⚠️ THE LOADING STATE, distinct from empty and from error.
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={fetchNextPage}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one.
                filterTerm={searching ? keyword : undefined}
                noun={'top level domain'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchNextPage}
            />

        </SimpleGrid>
    );
};
